import { apiFetch } from '../lib/apiFetch';

export interface TreeSpecies {
  id: string;
  name: string;
  latinName: string | null;
  description: string | null;
  isActive: boolean;
  workshopTypeCount: number;
  treeReplacementCount: number;
  createdAt: string;
  updatedAt: string;
}

export type TreeSpeciesInput = Pick<TreeSpecies, 'name' | 'latinName' | 'description'>;

export type TreeSpeciesPatch = Partial<TreeSpeciesInput & Pick<TreeSpecies, 'isActive'>>;

/** A refused request, keeping the status and any per-field messages. */
export class TreeSpeciesApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'TreeSpeciesApiError';
  }
}

const FALLBACK_MESSAGES: Record<number, string> = {
  403: "You don't have permission to change tree species.",
  404: 'That tree species no longer exists.',
};

async function throwRefusal(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    fieldErrors?: Record<string, string>;
  };
  const message =
    FALLBACK_MESSAGES[res.status] ?? body.message ?? `${fallback} (HTTP ${res.status})`;
  throw new TreeSpeciesApiError(res.status, message, body.fieldErrors);
}

async function readSpecies(res: Response, fallback: string): Promise<TreeSpecies> {
  if (!res.ok) return throwRefusal(res, fallback);
  return ((await res.json()) as { species: TreeSpecies }).species;
}

export async function fetchAllTreeSpecies(): Promise<TreeSpecies[]> {
  const res = await apiFetch('/api/v1/tree-species?status=all');
  if (!res.ok) return throwRefusal(res, 'Failed to load tree species');
  return ((await res.json()) as { species: TreeSpecies[] }).species;
}

export async function createTreeSpecies(input: TreeSpeciesInput): Promise<TreeSpecies> {
  const res = await apiFetch('/api/v1/tree-species', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return readSpecies(res, 'Failed to add the tree species');
}

export async function updateTreeSpecies(id: string, patch: TreeSpeciesPatch): Promise<TreeSpecies> {
  const res = await apiFetch(`/api/v1/tree-species/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return readSpecies(res, 'Failed to save the tree species');
}

export async function deleteTreeSpecies(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/tree-species/${id}`, { method: 'DELETE' });
  if (!res.ok) await throwRefusal(res, 'Failed to delete the tree species');
}
